import { useId } from 'react';
import { lookup } from '@stitch/pbx-adapter/describe';
import { KEY_TYPES, keyName, keyText, keyType, targetNoun, type KeyTarget, type PhoneKey, type PhoneModel } from '@stitch/pbx-adapter/phones';
import type { Directory } from '../api/client';
import { Icon } from '../lib/icons';
import { DestinationPicker } from './pickers';
import { whereIs } from './PhoneLayout';
import { Button, Field } from './ui';

/** Which directory entries a key type points at. */
const TARGET_KIND: Partial<Record<KeyTarget, string>> = {
  callPark: 'callPark',
  callQueue: 'callQueue',
  ringGroup: 'ringGroup',
  timeRule: 'timeRule',
  autoAttendant: 'autoAttendant'
};

const GROUPS = ['Calls', 'Call routing', 'Phone', 'Advanced'] as const;

/**
 * Edits one key: what it does, what it points at, and its label. Changes show on the phone
 * straight away; nothing is saved until the changes are reviewed.
 */
export function KeyEditor({
  model,
  keyId,
  value,
  onChange,
  dir,
  onClose,
  lineKeys
}: {
  model: PhoneModel;
  keyId: string;
  /** How many line keys the phone really has (as the picture draws them). */
  lineKeys?: number;
  /** The key as it is now; undefined = the phone's default. */
  value: PhoneKey | undefined;
  onChange: (k: PhoneKey | undefined) => void;
  dir: Directory | undefined;
  onClose: () => void;
}) {
  const id = useId();
  const type = value?.type ?? '';
  const t = type ? keyType(type) : undefined;
  const nameOf = (n: string) => (lookup(dir, n).kind === 'unknown' ? undefined : lookup(dir, n).name);
  const set = (patch: Partial<PhoneKey>) => {
    const next: PhoneKey = { key: keyId, type, ...value, ...patch };
    if (!next.type) return onChange(undefined);
    onChange(next);
  };
  const pickType = (v: string) => {
    if (!v) return onChange(undefined);
    const nt = keyType(v);
    // Keep the target only when the new kind of key points at the same kind of thing.
    const keep = nt?.target && t?.target === nt.target ? value?.target : undefined;
    onChange({ key: keyId, type: v, ...(keep ? { target: keep } : {}), ...(value?.label ? { label: value.label } : {}) });
  };
  const options = (kind: string) => (dir?.entries ?? []).filter((e) => e.kind === kind);
  const auto = value ? keyText({ ...value, label: '' }, nameOf) : '';

  return (
    <div className="key-editor" aria-labelledby={`${id}-title`}>
      <div className="key-editor-head">
        <div className="cell-stack">
          <strong id={`${id}-title`}>{keyName(keyId)}</strong>
          <small className="muted">{whereIs(model, keyId, lineKeys)}</small>
        </div>
        <button type="button" className="icon-btn" aria-label="Done with this key" title="Done" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>

      {value?.type === 'other' ? (
        <p className="muted">This key was set up in the phone system ({value.raw}) in a way Stitch doesn’t change. Choose something below to replace it, or leave it as it is.</p>
      ) : null}

      <div className="field">
        <label htmlFor={`${id}-type`}>What it does</label>
        <select id={`${id}-type`} className="input" value={type === 'other' ? '' : type} onChange={(e) => pickType(e.target.value)} data-autofocus>
          <option value="">Phone’s default</option>
          {GROUPS.map((g) => (
            <optgroup key={g} label={g}>
              {KEY_TYPES.filter((k) => k.group === g).map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <span className="hint">{t ? t.description : 'The phone decides (usually nothing, or another line).'}</span>
      </div>

      {t?.target === 'person' && <DestinationPicker label="Who" mode="people" value={value?.target ?? ''} onChange={(v) => set({ target: v })} dir={dir} clearable={false} />}
      {t?.target && TARGET_KIND[t.target] && (
        <div className="field">
          <label htmlFor={`${id}-target`}>
            {targetNoun(t.target)
              .replace(/^an? /, '')
              .replace(/^./, (c) => c.toUpperCase())}
          </label>
          <select id={`${id}-target`} className="input" value={value?.target ?? ''} onChange={(e) => set({ target: e.target.value })}>
            <option value="">Choose…</option>
            {options(TARGET_KIND[t.target]!).map((e) => (
              <option key={e.extension} value={e.extension}>
                {e.name || e.extension} ({e.extension})
              </option>
            ))}
          </select>
          {options(TARGET_KIND[t.target]!).length === 0 && <span className="hint">There aren’t any yet.</span>}
        </div>
      )}
      {(t?.target === 'number' || t?.target === 'optionalNumber') && (
        <Field
          label={t.target === 'optionalNumber' ? 'Number (optional)' : 'Number'}
          value={value?.target ?? ''}
          onChange={(e) => set({ target: e.target.value.replace(/[^0-9*#+]/g, '') })}
          inputMode="tel"
          placeholder={t.value === 'voicemail' ? 'Your own mailbox' : '+14105550123 or 201'}
        />
      )}
      {t?.target === 'url' && <Field label="Web address" value={value?.target ?? ''} onChange={(e) => set({ target: e.target.value.trim() })} placeholder="https://" />}
      {t?.target === 'digits' && (
        <Field label="Digits to send" value={value?.target ?? ''} onChange={(e) => set({ target: e.target.value.replace(/[^0-9*#]/g, '') })} inputMode="tel" />
      )}

      {value && value.type !== 'blank' && (
        <Field
          label="Label on the phone"
          value={value.label ?? ''}
          maxLength={20}
          onChange={(e) => set({ label: e.target.value })}
          placeholder={auto || 'Shown next to the key'}
          hint={value.label ? undefined : auto ? `Leave empty to show “${auto}”.` : undefined}
        />
      )}

      <div className="key-editor-foot">
        <Button size="small" variant="ghost" icon="undo" onClick={() => onChange(undefined)} disabled={!value}>
          Back to the phone’s default
        </Button>
      </div>
    </div>
  );
}
